import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import StakeLoading from "@/components/StakeLoading";
import NoData from "@/components/NoData";
import OKMatchLeagues from "../OKMatchLeagues";
import OKMatch from "./OKMatch";
import OKDateTabs from "../OKDateTabs";
import LoadMoreButton from "../LoadMoreButton";
import BookmakerSelectV8 from "../BookmakerSelectV8";
import { BANG_KEO_PAGE_LIMIT, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { CHANGE_BOOKMAKER_EXTEND, SET_TOGGLE_NEWBIE } from "@/lib/redux/reducers/actionTypes";
import KbvTiLeV27HeaderRight from "./KbvTiLeV27HeaderRight";
import LeagueFilterV27QuickChipsBar from "./LeagueFilterV27/LeagueFilterV27QuickChipsBar";
import { buildThemeStyle } from "./buildThemeStyle";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV27 = ({ type = "all" }) => {
  const dispatch = useDispatch();

  const compConfig = useCompConfig();
  const { hasDataConfig } = compConfig;
  const themeStyle = buildThemeStyle(compConfig, hasDataConfig);

  const [selectedDate, setSelectedDate] = useState(() =>
    dayjs().tz(TZ).format("YYYY-MM-DD"),
  );
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const newbieMode = useSelector((state) => state.toggleReducer.newbie);
  const bookmarkerExtend = useSelector((state) => state.bookmarkerExtendReducer);
  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `${item.shortName}`,
      })),
    [],
  );

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const baseLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();

  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => {
      clearAllData();
    };
  }, [clearAllData]);

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setSelectedLeagueIds([]);
  };

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
  };

  return (
    <div
      className="mx-auto flex min-h-screen flex-col gap-3 rounded-xl px-0.5 md:px-2 py-3 bg-[var(--kbv-bg-page)]"
      style={themeStyle}
    >
      <div className="relative overflow-auto mr-0.5 mb-4 box-border w-full rounded-xl border kbv-card max-md:mb-0 max-md:h-[49px] max-md:rounded-t-[14px] max-md:rounded-b-none max-md:shadow-none max-md:px-2.5 md:max-[1056px]:h-[55px] md:max-[1056px]:px-5 min-[1057px]:h-16 min-[1057px]:px-[30px]">
        <div className="flex h-full w-full min-w-0 gap-2 flex-row md:items-center md:justify-between">
          <div className="flex h-full min-w-0 flex-1 items-center max-md:mt-0 mt-[4px]">
            <OKDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              compact
            />
          </div>
          <div className="hidden md:block">
            <KbvTiLeV27HeaderRight
              newbieMode={newbieMode}
              onNewbieModeChange={(val) => {
                dispatch({ type: SET_TOGGLE_NEWBIE, payload: val });
              }}
              bookmarkerExtend={bookmarkerExtend}
              onBookmakerExtendChange={(val) =>
                dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
              }
              bookmakerExtendOptions={bookmakerExtendOptions}
            />
          </div>
        </div>
      </div>

      <LeagueFilterV27QuickChipsBar
        selectedIds={selectedLeagueIds}
        onChange={handleLeagueFilterChange}
        className="w-full min-w-0 px-0.5 md:px-0"
      />

      <div className="md:hidden sticky top-0 z-20 flex h-[49px] items-center justify-between border-b kbv-bg-white px-2.5 shadow-[0_3px_3px_rgba(0,0,0,0.3)]">
        <div className="flex items-center gap-3">
          <KbvTiLeV27HeaderRight
            newbieMode={newbieMode}
            onNewbieModeChange={(val) => {
              dispatch({ type: SET_TOGGLE_NEWBIE, payload: val });
            }}
            bookmarkerExtend={bookmarkerExtend}
            onBookmakerExtendChange={(val) =>
              dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
            }
            bookmakerExtendOptions={bookmakerExtendOptions}
            small
          />
          <div className="md:hidden">
            <BookmakerSelectV8
              value={bookmarkerExtend}
              onChange={(val) =>
                dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
              }
              options={bookmakerExtendOptions}
              scopeClassName="!text-[12px]"
            />
          </div>
        </div>
      </div>

      {loading && <StakeLoading />}
      {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
      {!loading && (
        <>
          <div className="space-y-2 rounded-lg ">
            {filteredLeaguesWithMatches.length === 0 ? (
              <NoData
                message={
                  selectedLeagueIds.length > 0
                    ? "Không tìm thấy trận đấu theo tiêu chí lọc"
                    : "Không có trận đấu nào"
                }
              />
            ) : (
              filteredLeaguesWithMatches.map(({ league, matches }, leagueIndex) => (
                <OKMatchLeagues
                  key={league?.id || `league-${leagueIndex}`}
                  league={league || {}}
                  matches={matches}
                  render={(match, idx) => (
                    match ? <OKMatch key={match.id || `${league?.id || leagueIndex}-${match?.matchTime || Math.random()}`} match={match} isFirstMatch={idx === 0} /> : null
                  )}
                />
              ))
            )}
          </div>
          <LoadMoreButton
            isLoading={isLoadingMore}
            onClick={handleLoadMore}
            loadingComponent={<StakeLoading />}
            hasMore={hasMore}
          />
        </>
      )}
    </div>
  );
};

export default KbvTiLeV27;
